// Lab Application History Page - Displays detailed history for a specific application
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Card,
    CardContent,
    Breadcrumbs,
    Link,
    Divider,
    Grid
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    History as HistoryIcon,
    Timeline as TimelineIcon,
    Assignment as AssignmentIcon
} from '@mui/icons-material';
import DoApiService from '../../../services/DoApiService';
import { decryptAppId } from '../../../utils/encryption';

const LabHistoryPage = () => {
    // State management
    const [historyData, setHistoryData] = useState([]);
    const [applicationInfo, setApplicationInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Router hooks
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get encrypted app ID from URL parameters
    const encryptedAppId = searchParams.get('appId');
    const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;


    // Load application history data
    const loadHistoryData = async () => {
        if (!appId) {
            setError('Invalid application ID. Please check the URL.');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Load detailed history data
            const historyResponse = await DoApiService.getLabApplicationHistory({ appId });

            if (historyResponse.success) {
                setHistoryData(historyResponse.data || []);
            } else {
                throw new Error(historyResponse.message || 'Failed to load application history');
            }

            // Also try to load basic application info for context
            try {
                const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                const appResponse = await DoApiService.getApplicationDetails({
                    appId,
                    orgId: userData.orgId,
                    userTypeId: userData.userTypeId
                });

                if (appResponse.success) {
                    setApplicationInfo(appResponse.data);
                }
            } catch (appError) {
                console.warn('Could not load application details:', appError);
            }

        } catch (err) {
            setError(`Error loading application history: ${err.message}`);
            console.error('Load history error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Load data when component mounts or appId changes
    useEffect(() => {
        loadHistoryData();
    }, [appId]);

    // Format date for display
    const formatDate = (dateString) => {
        return DoApiService.formatDate(dateString, 'datetime');
    };



    // Handle back navigation
    const handleBackClick = () => {
        navigate(-1); // Go back to previous page
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={handleBackClick}
                    sx={{ textDecoration: 'none' }}
                >
                    DO Applications
                </Link>
                <Typography variant="body2" color="text.primary">
                    Application History
                </Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackClick}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h4" sx={{ fontWeight: 'medium' }}>
                    Lab Application History
                </Typography>
            </Box>

            {/* Application Info Card */}
            {applicationInfo && (
                <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Application ID:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                                    {applicationInfo.applicationNo || appId}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Laboratory Name:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                    {applicationInfo.laboratoryName}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Contact Person:
                                </Typography>
                                <Typography variant="body1">
                                    {applicationInfo.contactPerson || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Application Date:
                                </Typography>
                                <Typography variant="body1">
                                    {applicationInfo.applicationDate.toLocaleDateString()}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
                        Loading application history...
                    </Typography>
                </Box>
            )}

            {/* History Table */}
            {!loading && historyData.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TimelineIcon sx={{ mr: 1, color: 'secondary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                            Process Timeline
                        </Typography>
                        <Chip
                            label={`${historyData.length} entries`}
                            size="small"
                            sx={{ ml: 2 }}
                            color="primary"
                            variant="outlined"
                        />
                    </Box>

                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: 3,
                            borderRadius: 2,
                            maxHeight: '70vh',
                            overflow: 'auto'
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            width: 60
                                        }}
                                    >
                                        #
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 150
                                        }}
                                    >
                                        Date & Time
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 200
                                        }}
                                    >
                                        Action/Remarks
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 50
                                        }}
                                    >
                                        Stage
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyData.map((history, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#fafafa'
                                            },
                                            '&:hover': {
                                                backgroundColor: '#f0f8ff'
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {index + 1}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {/* {formatDate(history.createdDate || history.actionDate || history.date)} */}
                                                {history.createdDate || ""}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                {history.remarks || history.action || history.comments || 'No remarks'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={history.status || history.stage || 'N/A'}
                                                size="small"
                                                color='primary'
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
                        Showing {historyData.length} history entries for Application ID: {appId}
                    </Typography>
                </>
            )}

            {/* No Data State */}
            {!loading && historyData.length === 0 && !error && (
                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <AssignmentIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                        <Typography variant="h6" color="textSecondary">
                            No History Found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            No history records are available for this application.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={loadHistoryData}
                            startIcon={<HistoryIcon />}
                            sx={{ mt: 2 }}
                        >
                            Refresh History
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default LabHistoryPage;